import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import DonateModal from "@/components/DonateModal";
import ReactionModal from "@/components/ReactionModal";
import ReportModal from "@/components/ReportModal";
import StarRating from "@/components/StarRating";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, BookOpen, Heart, ArrowLeft, MessageCircle, Star, Flag } from "lucide-react";
import { toast } from "sonner";

export default function WorkDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [work, setWork] = useState(null);
  const [donateOpen, setDonateOpen] = useState(false);
  const [reactionOpen, setReactionOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [wall, setWall] = useState([]);

  const loadWork = () => {
    api.get(`/works/${id}`).then(({ data }) => setWork(data)).catch((e) => {
      console.error("Load work failed", e);
      toast.error(formatApiError(e));
      nav("/browse");
    });
  };

  useEffect(() => { loadWork(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  useEffect(() => {
    if (work && (work.is_author || work.has_purchased)) {
      api.get(`/works/${id}/reactions/wall`).then(({ data }) => setWall(data)).catch((err) => {
        console.error("Load reactions wall failed", err);
        setWall([]);
      });
    }
  }, [work, id]);

  const buy = async () => {
    if (!user || user === false) {
      nav(`/auth?next=/work/${id}`);
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        work_id: id,
        kind: "purchase",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBuying(false);
    }
  };

  const triggerDonate = () => {
    if (!user || user === false) { nav(`/auth?next=/work/${id}`); return; }
    setDonateOpen(true);
  };

  if (!work) return <div className="min-h-screen grain"><Nav /></div>;

  const price = (work.price_cents / 100).toFixed(2);
  const unlocked = work.is_author || work.has_purchased;
  const coverUrl = work.cover_path ? `${API}/files/${work.cover_path}` : null;

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-[#6E675E] hover:text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest mb-8 transition-colors duration-200" data-testid="work-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to archive
        </button>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14">
          {/* Manuscript column */}
          <article>
            {coverUrl && (
              <div className="mb-6 rounded-sm overflow-hidden border border-[rgba(217,180,114,0.2)] max-h-[420px]">
                <img src={coverUrl} alt={work.title} className="w-full h-full object-cover" data-testid="work-cover-image" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
              </div>
            )}
            <div className="flex items-center gap-3 mb-4">
              <span className="chip">{work.genre}</span>
              <span className="font-mono-brass text-xs text-[#6E675E] uppercase tracking-widest">{work.work_type.replace("_", " ")} · {work.completion_percent}% draft</span>
            </div>

            <h1 className="font-serif-display text-4xl sm:text-6xl leading-[1.05] tracking-tight text-[#F7F3EC] mb-6" data-testid="work-title">
              {work.title}
            </h1>

            <div className="hairline mb-8" />

            <section className="paper-scroll rounded-sm p-6 sm:p-10 border border-[rgba(217,180,114,0.12)]" data-testid="excerpt-reader-preview">
              <div className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass mb-4">Free Excerpt</div>
              <div className="font-serif-body text-lg leading-[1.85] text-[#F7F3EC] whitespace-pre-wrap">
                {work.excerpt}
              </div>

              <div className="hairline my-10" />

              {unlocked ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-[#E6B860] font-mono-brass mb-4">Full Manuscript · Unlocked</div>
                  <div className="font-serif-body text-lg leading-[1.85] text-[#F7F3EC] whitespace-pre-wrap" data-testid="full-manuscript">
                    {work.full_content}
                  </div>
                </>
              ) : (
                <div className="relative" data-testid="locked-manuscript">
                  <div className="locked-content font-serif-body text-lg leading-[1.85] text-[#A8A095]">
                    The manuscript continues for another {work.word_count.toLocaleString()} words. The cartographer's letters, the seventeen doors, the correspondence with the ghost — all sealed behind the vault.
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-transparent via-[#161412]/80 to-[#161412]">
                    <Lock className="w-6 h-6 text-[#D9B472] mb-3" />
                    <div className="font-serif-display text-xl text-[#F7F3EC] mb-1">The vault is closed</div>
                    <div className="text-sm text-[#A8A095] mb-5 font-serif-body">Unlock the full manuscript for ${price}</div>
                    <button onClick={buy} disabled={buying} className="btn-buy px-6 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold" data-testid="unlock-inline-button">
                      {buying ? "Redirecting…" : `Unlock · $${price}`}
                    </button>
                  </div>
                </div>
              )}
            </section>

            {unlocked && (
              <section className="mt-10" data-testid="reactions-wall">
                <div className="flex items-center gap-2 mb-4">
                  <MessageCircle className="w-4 h-4 text-[#D9B472]" />
                  <h2 className="font-serif-display text-2xl text-[#F7F3EC]">The Marginalia</h2>
                  <span className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass ml-2">Lines other readers carried away</span>
                </div>
                {wall.length === 0 ? (
                  <p className="text-[#6E675E] font-serif-body italic text-sm">
                    No one has left a note yet. {work.has_purchased && !work.is_author ? "Be the first — the author would love it." : ""}
                  </p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {wall.map((r) => (
                      <blockquote key={r.id} className={`paper-scroll border-l-2 pl-5 py-3 pr-4 rounded-sm relative ${r.starred ? "border-[#E6B860] bg-[rgba(217,180,114,0.05)]" : "border-[#D9B472]"}`} data-testid={`wall-reaction-${r.id}`}>
                        {work.is_author && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await api.post(`/reactions/${r.id}/star`);
                                const { data } = await api.get(`/works/${id}/reactions/wall`);
                                setWall(data);
                              } catch (err) {
                                console.error("Star reaction failed", err);
                                toast.error(formatApiError(err));
                              }
                            }}
                            className="absolute top-2 right-2 text-[#D9B472] hover:text-[#E6B860] transition-colors duration-200"
                            data-testid={`star-reaction-${r.id}`}
                            title={r.starred ? "Unstar" : "Star this note"}
                          >
                            <Star className={`w-4 h-4 ${r.starred ? "fill-[#E6B860]" : ""}`} />
                          </button>
                        )}
                        {r.starred && !work.is_author && (
                          <Star className="w-3.5 h-3.5 text-[#E6B860] fill-[#E6B860] absolute top-3 right-3" />
                        )}
                        <p className="font-serif-body italic text-[#F7F3EC] leading-relaxed mb-2 pr-6">"{r.line}"</p>
                        <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">— Anonymous reader · {new Date(r.created_at).toLocaleDateString()}{r.starred && <span className="text-[#E6B860] ml-2">· Author starred</span>}</div>
                      </blockquote>
                    ))}
                  </div>
                )}
              </section>
            )}
          </article>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 self-start space-y-6">
            <div className="gold-frame paper-scroll rounded-sm p-6">
              <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-2">Author (anonymous)</div>
              <Link to={`/author/${encodeURIComponent(work.author_pseudonym)}`} className="text-2xl font-serif-display text-[#D9B472] italic mb-1 block hover:text-[#E6B860] transition-colors duration-200" data-testid="work-author-pseudonym">{work.author_pseudonym}</Link>
              <div className="text-xs text-[#6E675E] font-mono-brass mb-3">Real identity sealed · <span className="underline">visit storefront →</span></div>
              <StarRating
                avg={work.avg_rating || 0}
                count={work.rating_count || 0}
                kind="work"
                id={work.id}
                canRate={work.has_purchased && !work.is_author}
                onRated={(agg) => setWork((w) => ({ ...w, avg_rating: agg.avg_rating, rating_count: agg.rating_count }))}
              />
            </div>

            <div className="gold-frame paper-scroll rounded-sm p-6">
              <div className="flex items-baseline justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Unlock price</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">{work.word_count.toLocaleString()} words</div>
              </div>
              <div className="font-mono-brass text-4xl text-[#F7F3EC] mb-6" data-testid="work-price">${price}</div>

              {work.is_author ? (
                <div className="text-sm text-[#A8A095] font-serif-body italic border border-[rgba(217,180,114,0.15)] rounded-sm p-4">
                  This is your work. View earnings on your dashboard.
                </div>
              ) : work.has_purchased ? (
                <div className="text-sm text-[#E6B860] font-serif-body border border-[rgba(217,180,114,0.25)] rounded-sm p-4">
                  You've unlocked this manuscript. Read below or tip the author.
                </div>
              ) : (
                <button
                  onClick={buy}
                  disabled={buying}
                  className="btn-buy w-full rounded-sm py-4 font-mono-brass text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 mb-3"
                  data-testid="buy-work-button"
                >
                  <BookOpen className="w-4 h-4" />
                  {buying ? "Redirecting to Stripe…" : `Unlock manuscript · $${price}`}
                </button>
              )}

              {!work.is_author && (
                <button
                  onClick={triggerDonate}
                  className="btn-donate w-full rounded-sm py-4 font-mono-brass text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                  data-testid="donate-button"
                >
                  <Heart className="w-4 h-4" />
                  Send anonymous tip
                </button>
              )}

              {work.has_purchased && !work.is_author && (
                <button
                  onClick={() => setReactionOpen(true)}
                  className="btn-ghost-gold w-full rounded-sm py-4 font-mono-brass text-xs uppercase tracking-widest flex items-center justify-center gap-2 mt-3"
                  data-testid="reaction-open-button"
                >
                  <MessageCircle className="w-4 h-4" />
                  Leave a one-line note
                </button>
              )}

              <p className="text-[10px] text-[#6E675E] mt-4 font-mono-brass text-center">Stripe secured · Test card 4242 4242 4242 4242</p>
            </div>

            {!work.is_author && (
              <button
                onClick={() => setReportOpen(true)}
                className="w-full text-[10px] uppercase tracking-widest text-[#6E675E] hover:text-[#8C3A34] font-mono-brass transition-colors duration-200 py-2 flex items-center justify-center gap-1.5"
                data-testid="report-work-button"
              >
                <Flag className="w-3 h-3" /> Report this work
              </button>
            )}
          </aside>
        </div>
      </div>
      {donateOpen && <DonateModal work={work} onClose={() => setDonateOpen(false)} />}
      {reactionOpen && <ReactionModal work={work} onClose={() => setReactionOpen(false)} onCreated={() => {
        api.get(`/works/${id}/reactions/wall`).then(({ data }) => setWall(data)).catch(() => {});
      }} />}
      {reportOpen && <ReportModal work={work} onClose={() => setReportOpen(false)} />}
    </div>
  );
}
